import { Fragment, useCallback, useEffect, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, Plus, Trash2, X } from "lucide-react";

import {
  FORMAT_KEYS,
  TOURNAMENT_FORMAT_KEYS,
  useTranslations,
  VARIANCE_KEYS,
  WEIGHT_KEYS,
} from "@/i18n";
import { useActivity, useActivityList, usePlayerList } from "@/store/selectors";
import { type ActivityParams, isTeamsParams } from "@/store/slices/activitySlice";
import { useStore } from "@/store/store";
import { type Activity, ActivityFormat, ActivityVariance, TournamentFormat, Weight } from "@/types";
import type { ActivityId } from "@/types/branded";
import {
  CardHeader,
  CardTitle,
  ChipToggleAll,
  ConfirmDeleteRow,
  ConfirmDeleteText,
  CountBadge,
  ErrorText,
  FieldColumns,
  FieldGroup,
  FieldGroupLabel,
  FloodEmptyState,
  GlassCard,
  GlassDivider,
  GlassInput,
  GlassNumberInput,
  GlassRow,
  HeroInput,
  ItemChip,
  ListGap,
  MiniToggle,
  MiniToggleDot,
  ModeCard,
  ModeCardGrid,
  ModeCardIcon,
  ModeCardLabel,
  PillBtn,
  PillBtnPrimary,
  RowActions,
  RowName,
  Segment,
  SegmentStrip,
  SmallIconBtn,
} from "@/ui/styled";
import { theme } from "@/ui/theme";

/** Typed arrays for iterating over const enum values */
const VARIANCES: ActivityVariance[] = [
  ActivityVariance.Low,
  ActivityVariance.Medium,
  ActivityVariance.High,
];

const FORMATS: ActivityFormat[] = [
  ActivityFormat.FreeForAll,
  ActivityFormat.Teams,
  ActivityFormat.Tournament,
];

const FORMAT_ICONS: Record<ActivityFormat, string> = {
  [ActivityFormat.FreeForAll]: "\u{1F3AF}",
  [ActivityFormat.Teams]: "\u{1F465}",
  [ActivityFormat.Tournament]: "\u{1F3C6}",
};

const TOURNAMENT_FORMATS: TournamentFormat[] = [
  TournamentFormat.SingleElimination,
  TournamentFormat.DoubleElimination,
  TournamentFormat.RoundRobin,
  TournamentFormat.Swiss,
];

const WEIGHTS: Weight[] = [Weight.Low, Weight.Normal, Weight.High, Weight.Double];

const isDuplicateActivity = (activities: Activity[], name: string): boolean => {
  const lower = name.trim().toLowerCase();
  return activities.some((a) => a.name.toLowerCase() === lower);
};

const defaultValues: ActivityParams = {
  name: "",
  variance: ActivityVariance.Medium,
  format: ActivityFormat.FreeForAll,
  weight: Weight.Normal,
  participantIds: [],
  topK: 0,
};

// ── Activity item ──────────────────────────────────────────────

const ActivityItem = ({ activityId }: { activityId: ActivityId }) => {
  const updateActivityAction = useStore((s) => s.updateActivity);
  const deleteActivityAction = useStore((s) => s.deleteActivity);
  const activity = useActivity(activityId);
  const players = usePlayerList();
  const { t } = useTranslations();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Auto-reset confirm state
  useEffect(() => {
    if (!confirmDelete) return;
    const timer = setTimeout(() => setConfirmDelete(false), 2500);
    return () => clearTimeout(timer);
  }, [confirmDelete]);

  const onSubmit = useCallback(
    ({ value }: { value: ActivityParams }) => {
      updateActivityAction(activityId, value);
      setEditing(false);
    },
    [activityId, updateActivityAction],
  );

  const editForm = useForm({
    defaultValues: (activity as ActivityParams) ?? defaultValues,
    onSubmit,
  });

  if (activity == null) return null;

  const handleDelete = () => {
    if (confirmDelete) {
      deleteActivityAction(activity.id);
    } else {
      setConfirmDelete(true);
    }
  };

  // ── Confirm delete state ───────────────────────────────────
  if (confirmDelete) {
    return (
      <ConfirmDeleteRow>
        <ConfirmDeleteText>{t("activity.confirmDelete", activity.name)}</ConfirmDeleteText>
        <RowActions>
          <PillBtn
            type="button"
            onClick={() => setConfirmDelete(false)}
            style={{ fontSize: "0.75rem", padding: "0.25rem 0.625rem" }}
          >
            {t("activity.cancel")}
          </PillBtn>
          <SmallIconBtn style={{ color: theme.colors.danger }} onClick={handleDelete}>
            <Trash2 size={13} />
          </SmallIconBtn>
        </RowActions>
      </ConfirmDeleteRow>
    );
  }

  // ── Editing state — card with inline form ──────────────────
  if (editing) {
    return (
      <GlassCard>
        {/* Hero name + close */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <editForm.Field name="name">
            {(field) => (
              <HeroInput
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t("activity.addPlaceholder")}
              />
            )}
          </editForm.Field>
          <SmallIconBtn onClick={() => setEditing(false)} style={{ flexShrink: 0 }}>
            <X size={15} />
          </SmallIconBtn>
        </div>

        <div
          style={{
            padding: "0.75rem 0 0.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.875rem",
          }}
        >
          <GlassDivider />

          {/* ── GAME MODE ─────────────────────────────── */}
          <FieldGroup>
            <FieldGroupLabel>{t("activity.format")}</FieldGroupLabel>

            <editForm.Field name="format">
              {(field) => (
                <ModeCardGrid>
                  {FORMATS.map((f) => (
                    <ModeCard
                      key={f}
                      type="button"
                      active={field.state.value === f}
                      onClick={() => {
                        field.handleChange(f);
                        switch (f) {
                          case ActivityFormat.FreeForAll:
                            editForm.setFieldValue("topK", 0);
                            break;
                          case ActivityFormat.Teams:
                            editForm.setFieldValue("numTeams", 2);
                            editForm.setFieldValue("teamSizes", [0, 0]);
                            editForm.setFieldValue("randomizeTeams", false);
                            editForm.setFieldValue("topK", 0);
                            break;
                          case ActivityFormat.Tournament:
                            editForm.setFieldValue(
                              "tournamentFormat",
                              TournamentFormat.SingleElimination,
                            );
                            break;
                        }
                      }}
                    >
                      <ModeCardIcon>{FORMAT_ICONS[f]}</ModeCardIcon>
                      <ModeCardLabel active={field.state.value === f}>
                        {t(FORMAT_KEYS[f])}
                      </ModeCardLabel>
                    </ModeCard>
                  ))}
                </ModeCardGrid>
              )}
            </editForm.Field>

            {/* Format-specific options */}
            <editForm.Subscribe selector={(s) => s.values.format}>
              {(format) => (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={format}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                    style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
                  >
                    {format === ActivityFormat.FreeForAll && (
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <label style={{ fontSize: "0.75rem", color: theme.colors.textMuted }}>
                          {t("activity.topK")}:
                        </label>
                        <editForm.Field name="topK">
                          {(field) => (
                            <GlassNumberInput
                              type="number"
                              min={0}
                              max={100}
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(Math.max(0, Number(e.target.value)))
                              }
                            />
                          )}
                        </editForm.Field>
                      </div>
                    )}

                    {format === ActivityFormat.Teams && (
                      <Fragment>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <label style={{ fontSize: "0.75rem", color: theme.colors.textMuted }}>
                            {t("activity.numTeams")}:
                          </label>
                          <editForm.Field name="numTeams">
                            {(field) => (
                              <GlassNumberInput
                                type="number"
                                min={2}
                                max={8}
                                value={field.state.value}
                                onChange={(e) => {
                                  const n = Math.max(2, Number(e.target.value));
                                  field.handleChange(n);
                                  const prev = editForm.getFieldValue("teamSizes");
                                  const next = [...prev];
                                  while (next.length < n) next.push(0);
                                  editForm.setFieldValue("teamSizes", next.slice(0, n));
                                }}
                              />
                            )}
                          </editForm.Field>
                        </div>

                        <editForm.Subscribe
                          selector={(s) =>
                            isTeamsParams(s.values)
                              ? { numTeams: s.values.numTeams, teamSizes: s.values.teamSizes }
                              : { numTeams: 0, teamSizes: [] as number[] }
                          }
                        >
                          {({ numTeams, teamSizes }) => (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              {Array.from({ length: numTeams }, (_, i) => (
                                <label
                                  key={i}
                                  style={{
                                    fontSize: "0.75rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    color: theme.colors.textMuted,
                                  }}
                                >
                                  {t("general.teamN", i + 1)}:
                                  <GlassNumberInput
                                    type="number"
                                    min={0}
                                    max={10}
                                    value={teamSizes[i] ?? 0}
                                    onChange={(e) => {
                                      const v = Math.max(0, Number(e.target.value));
                                      const next = [...teamSizes];
                                      next[i] = v;
                                      editForm.setFieldValue("teamSizes", next);
                                    }}
                                    style={{ width: 48 }}
                                  />
                                </label>
                              ))}
                              <span style={{ fontSize: "0.65rem", color: theme.colors.textDim }}>
                                (0 = auto)
                              </span>
                            </div>
                          )}
                        </editForm.Subscribe>

                        <editForm.Field name="randomizeTeams">
                          {(field) => (
                            <MiniToggle
                              type="button"
                              on={field.state.value}
                              onClick={() => field.handleChange(!field.state.value)}
                            >
                              <MiniToggleDot on={field.state.value} />
                              {t("activity.randomize")}
                            </MiniToggle>
                          )}
                        </editForm.Field>

                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <label style={{ fontSize: "0.75rem", color: theme.colors.textMuted }}>
                            {t("activity.topK")}:
                          </label>
                          <editForm.Field name="topK">
                            {(field) => (
                              <GlassNumberInput
                                type="number"
                                min={0}
                                max={100}
                                value={field.state.value}
                                onChange={(e) =>
                                  field.handleChange(Math.max(0, Number(e.target.value)))
                                }
                              />
                            )}
                          </editForm.Field>
                        </div>
                      </Fragment>
                    )}

                    {format === ActivityFormat.Tournament && (
                      <div>
                        <label
                          style={{
                            fontSize: "0.75rem",
                            color: theme.colors.textMuted,
                            display: "block",
                            marginBottom: "0.375rem",
                          }}
                        >
                          {t("activity.tournamentFormat")}:
                        </label>
                        <editForm.Field name="tournamentFormat">
                          {(field) => (
                            <SegmentStrip>
                              {TOURNAMENT_FORMATS.map((tf) => (
                                <Segment
                                  key={tf}
                                  type="button"
                                  active={field.state.value === tf}
                                  onClick={() => field.handleChange(tf)}
                                >
                                  {t(TOURNAMENT_FORMAT_KEYS[tf])}
                                </Segment>
                              ))}
                            </SegmentStrip>
                          )}
                        </editForm.Field>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </editForm.Subscribe>
          </FieldGroup>

          {/* ── TUNING ────────────────────────────────── */}
          <FieldGroup>
            <FieldGroupLabel>{t("activity.tuning")}</FieldGroupLabel>
            <FieldColumns>
              <div>
                <label
                  style={{
                    fontSize: "0.75rem",
                    color: theme.colors.textMuted,
                    display: "block",
                    marginBottom: "0.375rem",
                  }}
                >
                  {t("activity.variance")}
                </label>
                <editForm.Field name="variance">
                  {(field) => (
                    <SegmentStrip>
                      {VARIANCES.map((v) => (
                        <Segment
                          key={v}
                          type="button"
                          active={field.state.value === v}
                          onClick={() => field.handleChange(v)}
                        >
                          {t(VARIANCE_KEYS[v])}
                        </Segment>
                      ))}
                    </SegmentStrip>
                  )}
                </editForm.Field>
              </div>

              <div>
                <label
                  style={{
                    fontSize: "0.75rem",
                    color: theme.colors.textMuted,
                    display: "block",
                    marginBottom: "0.375rem",
                  }}
                >
                  {t("activity.weight")}
                </label>
                <editForm.Field name="weight">
                  {(field) => (
                    <SegmentStrip>
                      {WEIGHTS.map((w) => (
                        <Segment
                          key={w}
                          type="button"
                          active={field.state.value === (w as number)}
                          onClick={() => field.handleChange(w)}
                        >
                          {t(WEIGHT_KEYS[w])}
                        </Segment>
                      ))}
                    </SegmentStrip>
                  )}
                </editForm.Field>
              </div>
            </FieldColumns>
          </FieldGroup>

          {/* ── PLAYERS ───────────────────────────────── */}
          <FieldGroup>
            <FieldGroupLabel>{t("activity.participants")}</FieldGroupLabel>
            <editForm.Field name="participantIds">
              {(field) => {
                const ids = field.state.value ?? [];
                const allSelected = players.length > 0 && ids.length === players.length;

                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <ChipToggleAll
                      type="button"
                      allSelected={allSelected}
                      onClick={() =>
                        field.handleChange(allSelected ? [] : players.map((p) => p.id))
                      }
                    >
                      {allSelected ? "\u2715" : "\u2713"}{" "}
                      {allSelected ? t("activity.deselectAll") : t("activity.selectAll")}
                    </ChipToggleAll>
                    {players.map((p) => (
                      <ItemChip
                        key={p.id}
                        active={ids.includes(p.id)}
                        onClick={() =>
                          field.handleChange(
                            ids.includes(p.id) ? ids.filter((pid) => pid !== p.id) : [...ids, p.id],
                          )
                        }
                      >
                        {p.name}
                      </ItemChip>
                    ))}
                  </div>
                );
              }}
            </editForm.Field>
          </FieldGroup>

          {/* ── Actions ───────────────────────────────── */}
          <div style={{ display: "flex", gap: "0.375rem" }}>
            <PillBtnPrimary type="button" onClick={() => editForm.handleSubmit()}>
              {t("activity.save")}
            </PillBtnPrimary>
            <PillBtn type="button" onClick={() => setEditing(false)}>
              {t("activity.cancel")}
            </PillBtn>
          </div>
        </div>
      </GlassCard>
    );
  }

  // ── Default state — compact row with emoji icon ────────────

  return (
    <GlassRow>
      <span style={{ fontSize: "1rem", flexShrink: 0, lineHeight: 1 }}>
        {FORMAT_ICONS[activity.format ?? ActivityFormat.FreeForAll]}
      </span>
      <RowName>{activity.name}</RowName>

      <RowActions>
        <SmallIconBtn
          onClick={() => {
            editForm.reset();
            setEditing(true);
          }}
        >
          <Pencil size={13} />
        </SmallIconBtn>
        <SmallIconBtn style={{ color: theme.colors.danger }} onClick={handleDelete}>
          <Trash2 size={13} />
        </SmallIconBtn>
      </RowActions>
    </GlassRow>
  );
};

// ── Activity list (wrapper) ────────────────────────────────────

const ActivityList = () => {
  const addActivityAction = useStore((s) => s.addActivity);
  const activities = useActivityList();
  const { t } = useTranslations();

  const addForm = useForm({
    defaultValues: defaultValues,
    onSubmit: ({ value }: { value: ActivityParams }) => {
      addActivityAction(value);
      addForm.reset();
    },
  });

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>
          {t("activity.title")}
        </CardTitle>
        {activities.length > 0 && <CountBadge>{activities.length}</CountBadge>}
      </CardHeader>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await addForm.handleSubmit();
        }}
        style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}
      >
        <addForm.Field
          name="name"
          validators={{
            onSubmit: ({ value }) => {
              const trimmed = value.trim();
              if (!trimmed) return t("general.required");
              if (isDuplicateActivity(activities, trimmed))
                return t("general.alreadyExists", trimmed);
              return undefined;
            },
          }}
        >
          {(field) => (
            <GlassInput
              type="text"
              placeholder={t("activity.addPlaceholder")}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              autoComplete="off"
            />
          )}
        </addForm.Field>

        <addForm.Subscribe selector={(s) => s.values.name}>
          {(name) => (
            <PillBtnPrimary type="submit" disabled={!name.trim()} style={{ flexShrink: 0 }}>
              <Plus size={14} />
            </PillBtnPrimary>
          )}
        </addForm.Subscribe>
      </form>

      <addForm.Subscribe selector={(s) => s.fieldMeta.name?.errors}>
        {(errors) => (errors?.length ? <ErrorText>{errors.join(", ")}</ErrorText> : null)}
      </addForm.Subscribe>

      {activities.length === 0 ? (
        <FloodEmptyState>{t("match.needActivities")}</FloodEmptyState>
      ) : (
        <ListGap>
          {activities.map((a) => (
            <ActivityItem key={a.id} activityId={a.id} />
          ))}
        </ListGap>
      )}
    </GlassCard>
  );
};

export default ActivityList;
